import { loadInternalCalendar as loadCalendar } from './general/utils/consultation/calendar';
import serializeForm from './general/utils/consultation/serialize-form';
import { validate } from './general/utils/validation';
import { AppointmentSlot, AppointmentStatus, AppointmentType, CalendarType, InsuranceGroup, Language } from './types/consultation';
import { getAppointmentName, prepareDateAndTime } from './general/utils/consultation/appointment-helpers';

export enum SalesforceProduct {
  Comprehensive = 'Vollversicherung',
  ComprehensiveExpat = 'Vollversicherung Expat',
  ComprehensiveEmployeeOrSelfEmployed = 'Vollversicherung Angestellte Selbststaendige',
  ComprehensiveStudent = 'Vollversicherung Student',
  Dental = 'Zahnzusatzversicherung',
  CivilServant = 'Beihilfe Beamte',
  CivilServantApplicant = 'Beihilfe Beamtenanwaerter',
  CivilServantBroker = 'Beihilfeversicherung',
  Clinic = 'Krankenhauszusatzversicherung',
  CompanyHealthInsurance = 'Betriebliche Krankenversicherung'
}

export enum SalesforceServiceProduct {
  Comprehensive = 'comprehensive',
  ComprehensiveExpat = 'comprehensive-expat',
  ComprehensiveStudent = 'comprehensive-student',
  CivilServant = 'civil-servant',
  CivilServantApplicant = 'civil-servant-applicant',
  Dental = 'dental',
  Clinic = 'clinic',
  CivilServantBroker = 'civil-servant-broker',
  General = 'general'
}

const appointmentUrl = window.craftConfig.global.appointmentServiceUrl;

type AppointmentDetails = {
  calendar_type: CalendarType;
  status: string;
  start: string;
  id: string;
  type: AppointmentType;
};

document.querySelectorAll('.internal-consultation').forEach(block => {
  let calendar: CalendarType;
  let reschedulingCalendar: CalendarType;
  let isSubmitting = false;

  // APPOINTMENT TYPE SELECT
  const appointmentTypeSelect = block.querySelector('select[name="product"]') as HTMLSelectElement;
  const searchAppointmentButton = block.querySelector('button[data-action="search-appointments"]') as HTMLButtonElement;
  let lastTypeSearch: null | string = null;

  // RADIO SWITCH
  const radioButtons = block.querySelectorAll('input[type="radio"]') as NodeListOf<HTMLInputElement>;

  // RESCHEDULE SLIDE
  const rescheduleSlide = block.querySelector('.reschedule-appointment.slide') as HTMLElement;
  const appointmentsWrapper = block.querySelector('.appointments-wrapper') as HTMLElement;
  const appointmentPlaceholderText = block.querySelector('.appointments-placeholder') as HTMLElement;
  const appointmentPlaceholderInitText = appointmentPlaceholderText.textContent as string;
  const noAppointmentsText = block.querySelector('.no-appointments-text') as HTMLElement;
  const cancellationModal = block.querySelector('.modal.cancellation-modal') as HTMLElement;
  const rescheduleModal = block.querySelector('.modal.reschedule-modal') as HTMLElement;
  const rescheduleAppointmentForm = rescheduleModal.querySelector('form') as HTMLFormElement;
  const rescheduleAppointmentLanguage = rescheduleAppointmentForm.querySelector('select[name="language"]') as HTMLSelectElement;
  const rescheduleCalendarType = rescheduleModal.querySelector('.rebook-calendar-type') as HTMLInputElement;
  const rescheduleAppointmentFormDate = rescheduleAppointmentForm.querySelector('.dateSelect') as HTMLSelectElement;
  const rescheduleAppointmentFormTime = rescheduleAppointmentForm.querySelector('.timeSelect') as HTMLSelectElement;
  const rescheduleAppointmentAlert = block.querySelector('.reschedule-alert.alert') as HTMLElement;

  // NEW APPOINTMENT SLIDE
  const newAppointmentSlide = block.querySelector('.new-appointment.slide') as HTMLElement;
  const newAppointmentForm = block.querySelector('.new-appointment form') as HTMLFormElement;
  const newAppointmentSubmit = newAppointmentForm.querySelector('button[type="submit"]') as HTMLButtonElement;
  const newConsultationAlert = block.querySelector('.new-appointment.alert') as HTMLElement;
  const calendarOutput = block.querySelector('[data-calendar]') as HTMLSpanElement;

  const url = window.location.href;
  const parameters = url.indexOf('?') > -1 ? url.split('?')[1].split('&') : '';
  let parametersObject: { [key: string]: string } = {};
  if (parameters !== '') {
    for (const index in parameters) {
      const parameterDetails = parameters[index].split('=');
      parametersObject[parameterDetails[0]] = decodeURIComponent(parameterDetails[1]);
    }
  }

  // if not opportunity we should not allow booking underwriting
  if (!parametersObject.opportunity_id) {
    const underwritingType = appointmentTypeSelect.querySelector('option[value="underwriting"]') as HTMLOptionElement;
    underwritingType.setAttribute('disabled', '');
  }

  if (!parametersObject.upselling) {
    const upsellingType = appointmentTypeSelect.querySelector('option[value="upselling"]') as HTMLOptionElement;
    upsellingType.setAttribute('disabled', '');
  }

  let searchParamsProduct: SalesforceServiceProduct;
  let handledAppointment: AppointmentDetails | null = null;

  const userAppointments = new Map([
    [AppointmentType.Consultation, fetchAppointmentDetails(parametersObject.consultation_appointment_id)],
    [AppointmentType.Idd, fetchAppointmentDetails(parametersObject.idd_appointment_id)],
    [AppointmentType.Underwriting, fetchAppointmentDetails(parametersObject.uw_appointment_id)],
    [AppointmentType.Signup, fetchAppointmentDetails(parametersObject.signup_appointment_id)],
    [AppointmentType.FollowUp, fetchAppointmentDetails(parametersObject.followup_appointment_id)],
    [AppointmentType.Upselling, fetchAppointmentDetails(parametersObject.upselling_appointment_id)],
    [AppointmentType.Bap, fetchAppointmentDetails(parametersObject.bap_appointment_id)]
  ]);

  // Handle "tab" switch
  radioButtons.forEach(button => {
    button.addEventListener('click', () => {
      rescheduleSlide.classList.toggle('active', rescheduleSlide.classList.contains(button.value));
      newAppointmentSlide.classList.toggle('active', newAppointmentSlide.classList.contains(button.value));
    });
  });

  // Enable and disable button based on selected appointment
  appointmentTypeSelect.addEventListener('change', () => {
    newAppointmentSubmit.disabled = true;
    searchAppointmentButton.disabled = appointmentTypeSelect.value === '' || appointmentTypeSelect.value === lastTypeSearch;
    updateAlert(newConsultationAlert, false);
    updateAlert(rescheduleAppointmentAlert, false);
  });

  // Handle appointment search submit
  searchAppointmentButton.addEventListener('click', async e => {
    e.preventDefault();

    lastTypeSearch = appointmentTypeSelect.value;
    searchAppointmentButton.disabled = true;
    newAppointmentSubmit.disabled = false;
    appointmentPlaceholderText.innerHTML = window.progressText;

    const appointment = await userAppointments.get(appointmentTypeSelect.value as AppointmentType);

    if (!appointment) {
      removeAppointment();
      if (appointmentPlaceholderInitText) {
        appointmentPlaceholderText.innerHTML = appointmentPlaceholderInitText;
      }
      updateSlides(false);
      return;
    }

    removeAppointment();
    appointmentsWrapper.appendChild(createAppointmentItem(appointment));
    updateSlides(true);
  });

  // Update slides
  function updateSlides(appointmentFound: boolean) {
    radioButtons.forEach(button => (button.disabled = false));
    appointmentPlaceholderText.classList.toggle('hidden', appointmentFound);
    noAppointmentsText.classList.toggle('hidden', appointmentFound);
    rescheduleSlide.classList.toggle('active', appointmentFound);
    newAppointmentSlide.classList.toggle('active', !appointmentFound);
    radioButtons[0].checked = appointmentFound;
    radioButtons[1].checked = !appointmentFound;
  }

  // Create appointment card function
  function createAppointmentItem(appointment: AppointmentDetails) {
    const template = block.querySelector('template') as HTMLTemplateElement;
    const card = template.content.cloneNode(true) as HTMLDivElement;
    const headline = card.querySelector('h3.appointment-type') as HTMLElement;
    const calendarType = card.querySelector('.calendar-type') as HTMLElement;
    const details = card.querySelector('.appointment-details') as HTMLElement;
    const cancelButton = card.querySelector('button.red') as HTMLButtonElement;
    const rescheduleButton = card.querySelector('button.outline') as HTMLButtonElement;

    headline.textContent = getAppointmentName(appointment.calendar_type);
    calendarType.textContent = getCalendarType(appointment.type, appointment.calendar_type.includes('expat') ? Language.English : Language.German, searchParamsProduct);
    details.textContent = prepareDateAndTime(appointment.start);

    cancelButton.addEventListener('click', () => {
      handledAppointment = appointment;
      cancellationModal.classList.add('is-open');
      updateAlert(rescheduleAppointmentAlert, false);
    });

    rescheduleButton.addEventListener('click', async () => {
      rescheduleModal.classList.add('is-open');
      handledAppointment = appointment;
      rescheduleAppointmentLanguage.value = searchParams.get('language') === 'Englisch' ? Language.English : Language.German;
      updateRebookingCalendar();
      updateAlert(rescheduleAppointmentAlert, false);
    });

    return card;
  }

  // Remove "old" appointment
  function removeAppointment() {
    const prevCard = appointmentsWrapper.querySelector('.appointment-card');
    if (prevCard) {
      prevCard.remove();
    }
  }

  async function updateRebookingCalendar() {
    if (handledAppointment && rescheduleAppointmentFormDate) {
      reschedulingCalendar = getCalendarType(handledAppointment.type, rescheduleAppointmentLanguage.value as Language, searchParamsProduct);
      loadCalendar(reschedulingCalendar, rescheduleAppointmentFormDate, rescheduleAppointmentFormTime, false);
      rescheduleCalendarType.textContent = reschedulingCalendar;
    }
  }

  // CANCELLATION MODAL
  if (cancellationModal) {
    const abortButton = cancellationModal.querySelector('button[data-action="abort"]') as HTMLButtonElement;
    const confirmCancellationButton = cancellationModal.querySelector('button[data-action="cancel"]') as HTMLButtonElement;

    if (abortButton) {
      abortButton.addEventListener('click', () => {
        cancellationModal.classList.remove('is-open');
      });
    }
    if (confirmCancellationButton) {
      const initCancelText = confirmCancellationButton.innerHTML;
      confirmCancellationButton.addEventListener('click', async () => {
        if (!handledAppointment) {
          return;
        }
        confirmCancellationButton.disabled = true;
        confirmCancellationButton.innerHTML = window.progressText;
        const formData = serializeForm(rescheduleAppointmentForm);
        try {
          const response = await fetch(appointmentUrl + '/reschedule/internal', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              existing_appointment_id: handledAppointment.id,
              appointment_type: appointmentTypeSelect.value,
              contact_details: {
                first_name: parametersObject.first_name,
                last_name: parametersObject.last_name,
                email: parametersObject.email,
                landing_page_language: handledAppointment.calendar_type.includes('expat')
                  ? 'en'
                  : handledAppointment.type === AppointmentType.Underwriting
                    ? parametersObject.language.slice(0, 2).toLowerCase()
                    : 'de',
                ...(formData.phone_number && { phone_number: formData.phone_number })
              },
              metadata: {
                product_type: salesforceProductToSalesforceServiceProduct(parametersObject.product),
                ...(parametersObject.lead_id && { salesforce_lead_id: parametersObject.lead_id }),
                ...(parametersObject.opportunity_id && { salesforce_opportunity_id: parametersObject.opportunity_id }),
                ...(parametersObject.signup_service_id && { signup_service_id: parametersObject.signup_service_id }),
                ...(parametersObject.lead_service_id && { lead_service_id: parametersObject.lead_service_id }),
                ...(parametersObject.ottonova_tracking_id && { ottonova_tracking_id: parametersObject.ottonova_tracking_id })
              }
            })
          });
          if (!response.ok) {
            throw new Error(`${response.status}`);
          }
          userAppointments.set(appointmentTypeSelect.value as AppointmentType, fetchAppointmentDetails(handledAppointment.id));
          updateAlert(rescheduleAppointmentAlert, true, 'success', 'Termin erfolgreich storniert.');
          removeAppointment();
          appointmentPlaceholderText.innerHTML = appointmentPlaceholderInitText;
          appointmentPlaceholderText.classList.remove('hidden');
          confirmCancellationButton.disabled = false;
          cancellationModal.classList.remove('is-open');
          confirmCancellationButton.innerHTML = initCancelText;
        } catch (error) {
          updateAlert(rescheduleAppointmentAlert, true, 'danger', 'Es lag ein Problem beim Stornieren des Termins vor.');
          cancellationModal.classList.remove('is-open');
          confirmCancellationButton.innerHTML = initCancelText;
          confirmCancellationButton.disabled = false;
        }
      });
    }
  }

  // RESCHEDULE MODAL
  if (rescheduleModal) {
    const abortButton = rescheduleModal.querySelector('button[data-action="abort"]') as HTMLButtonElement;
    const rescheduleButton = rescheduleModal.querySelector('button[data-action="submit"]') as HTMLButtonElement;
    rescheduleAppointmentLanguage.addEventListener('change', () => {
      updateRebookingCalendar();
    });

    // ABORT RESCHEDULE
    if (abortButton) {
      abortButton.addEventListener('click', e => {
        e.preventDefault();
        rescheduleModal.classList.remove('is-open');
        handledAppointment = null;
      });
    }

    // SUBMIT RESCHEDULE
    if (rescheduleButton) {
      const initRescheduleText = rescheduleButton.innerHTML;
      rescheduleButton.addEventListener('click', async e => {
        e.preventDefault();
        if (!handledAppointment) {
          return;
        }

        if (validate.validateAll(rescheduleAppointmentForm).length) {
          return;
        }
        const formData = serializeForm(rescheduleAppointmentForm);
        rescheduleButton.innerHTML = window.progressText;
        rescheduleButton.disabled = true;
        const selectedTime = rescheduleAppointmentFormTime.options[rescheduleAppointmentFormTime.selectedIndex];
        const start = selectedTime.dataset.start || '';
        const end = selectedTime.dataset.end || '';

        try {
          const response = await fetch(appointmentUrl + '/reschedule/internal', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              existing_appointment_id: handledAppointment.id,

              appointment_type: handledAppointment.type,
              appointment: {
                start: start,
                end: end
              },
              calendar: reschedulingCalendar,
              contact_details: {
                first_name: parametersObject.first_name,
                last_name: parametersObject.last_name,
                email: parametersObject.email,
                landing_page_language: formData.language,
                phone_number: parametersObject.phone_number
              },
              metadata: {
                product_type: salesforceProductToSalesforceServiceProduct(parametersObject.product),
                ...(parametersObject.lead_id && { salesforce_lead_id: parametersObject.lead_id }),
                ...(parametersObject.opportunity_id && { salesforce_opportunity_id: parametersObject.opportunity_id }),
                ...(parametersObject.signup_service_id && { signup_service_id: parametersObject.signup_service_id }),
                ...(parametersObject.lead_service_id && { lead_service_id: parametersObject.lead_service_id }),
                ...(parametersObject.ottonova_tracking_id && { ottonova_tracking_id: parametersObject.ottonova_tracking_id })
              }
            })
          });
          if (!response.ok) {
            throw new Error(`${response.status}`);
          }
          const appointmentId = (await response.json()).appointment_id as string;

          rescheduleModal.classList.remove('is-open');
          updateAlert(rescheduleAppointmentAlert, true, 'success', 'Der Termin wurde erfolgreich umgebucht');
          rescheduleButton.innerHTML = initRescheduleText;
          rescheduleButton.disabled = false;
          userAppointments.set(handledAppointment.type, fetchAppointmentDetails(appointmentId));

          const newDetails = await userAppointments.get(handledAppointment.type);
          removeAppointment();
          if (newDetails) {
            appointmentsWrapper.appendChild(createAppointmentItem(newDetails));
            updateSlides(true);
          } else {
            updateSlides(false);
          }
        } catch (error) {
          rescheduleModal.classList.remove('is-open');
          updateAlert(rescheduleAppointmentAlert, true, 'danger', 'Es lag ein Problem beim Umbuchen des Termins vor');
          rescheduleButton.innerHTML = initRescheduleText;
        }
      });
    }
  }
  // INIT

  const updateAlert = (alert: HTMLElement, show: boolean, level?: 'danger' | 'warning' | 'success', message?: string) => {
    const content = alert.querySelector('.otto-alert-block-content') as HTMLElement;
    alert.classList.toggle('is-hidden', !show);
    if (level) {
      alert.classList.toggle('warning', level === 'warning');
      alert.classList.toggle('danger', level === 'danger');
      alert.classList.toggle('success', level === 'success');
    }

    if (message) {
      content.innerHTML = message;
    }
  };

  const searchParams = new URLSearchParams(window.location.search);
  const inputs: { [key: string]: HTMLInputElement | HTMLSelectElement } = Array.from(newAppointmentSlide.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input, select')).reduce<typeof inputs>(
    (inputsObject, input) => {
      inputsObject[input.name] = input;
      if (input.name === 'product') {
        const product = salesforceProductToSalesforceServiceProduct(searchParams.get('product'));

        searchParamsProduct = product;
        if (!product) {
          updateAlert(newConsultationAlert, true, 'warning', 'Der Benutzer hat bis jetzt noch nicht angegeben, für welches Produkt er sich interessiert.');
        } else {
          input.value = product;
        }
      } else if (input.name === 'language') {
        input.value = searchParams.get('language') === 'Englisch' ? Language.English : Language.German;
      } else {
        input.value = searchParams.get(input.name) || input.value;
      }
      input.dispatchEvent(new Event('update'));
      return inputsObject;
    },
    {}
  );

  const product = inputs['product'];
  const newConsultationFormDate = inputs['date'];
  const newConsultationFormTime = inputs['time'];
  const language = inputs['language'];

  if (product && language && newConsultationFormDate && newConsultationFormTime) {
    const updateAppointment = () => {
      calendar = getCalendarType(appointmentTypeSelect.value as AppointmentType, language.value as Language, product.value as SalesforceServiceProduct);
      loadCalendar(calendar, newConsultationFormDate, newConsultationFormTime, false);
      calendarOutput.innerText = calendar;
      if (
        ((product.value as SalesforceServiceProduct) === SalesforceServiceProduct.Comprehensive || (product.value as SalesforceServiceProduct) === SalesforceServiceProduct.Dental) &&
        (appointmentTypeSelect.value as AppointmentType) === AppointmentType.Upselling
      ) {
        newConsultationAlert.classList.add('force-keep-open');
        if (product instanceof HTMLSelectElement) {
          updateAlert(newConsultationAlert, true, 'warning', `Termin zu Umstellungen sind für Produkt "${product.options[product.selectedIndex].text}" nicht vorhergesehen.`);
        }
      } else {
        newConsultationAlert.classList.remove('force-keep-open');
      }
    };
    appointmentTypeSelect.addEventListener('change', updateAppointment);
    product.addEventListener('change', updateAppointment);
    language.addEventListener('change', updateAppointment);
  }

  function getCalendarType(appointmentType: AppointmentType, language: Language, product: SalesforceServiceProduct): CalendarType {
    let calendar: CalendarType = CalendarType.Sales;
    if (appointmentType === AppointmentType.Upselling) {
      calendar = language === Language.English || product === SalesforceServiceProduct.ComprehensiveExpat ? CalendarType.ExpatUpselling : CalendarType.Upselling;
    } else if (product !== SalesforceServiceProduct.Dental) {
      const englishCalendarCheck =
        (product === SalesforceServiceProduct.ComprehensiveExpat || language === Language.English) &&
        product !== SalesforceServiceProduct.CivilServant &&
        product !== SalesforceServiceProduct.CivilServantApplicant;
      if (appointmentType === AppointmentType.Idd) {
        calendar = englishCalendarCheck ? CalendarType.ExpatIdd : CalendarType.Idd;
      } else if (appointmentType === AppointmentType.FollowUp) {
        calendar = englishCalendarCheck ? CalendarType.ExpatFollowUp : CalendarType.FollowUp;
      } else if (appointmentType === AppointmentType.Signup) {
        calendar = englishCalendarCheck ? CalendarType.ExpatSignup : CalendarType.Signup;
      } else if (appointmentType === AppointmentType.Underwriting) {
        calendar = CalendarType.Underwriting;
      } else if (appointmentType === AppointmentType.Bap) {
        calendar = englishCalendarCheck ? CalendarType.ExpatBap : CalendarType.Bap;
      } else {
        calendar = englishCalendarCheck ? CalendarType.SalesExpat : CalendarType.Sales;
      }
    } else {
      calendar = appointmentType === AppointmentType.Underwriting ? CalendarType.Underwriting : CalendarType.SalesDental;
    }

    return calendar;
  }

  newAppointmentForm.addEventListener('submit', async ev => {
    ev.preventDefault();
    if (isSubmitting) {
      return;
    }
    isSubmitting = true;
    if (validate.validateAll(newAppointmentForm).length) {
      return;
    }

    if (await userAppointments.get(appointmentTypeSelect.value as AppointmentType)) {
      updateAlert(newConsultationAlert, true, 'warning', 'Es besteht bereits ein Termin dieser Terminart!');
      isSubmitting = false;
      return;
    }
    updateAlert(newConsultationAlert, false);
    const formValues = serializeForm(newAppointmentForm);
    // @ts-ignore
    newAppointmentSubmit.innerHTML = window.progressText;
    const selectedTimeOption = newConsultationFormTime.querySelector<HTMLElement>(`option[value="${formValues.time}"]`);
    if (!selectedTimeOption) {
      isSubmitting = false;
      return false;
    }

    if (product.value === InsuranceGroup.Expat && language.value === Language.German) {
      updateAlert(newConsultationAlert, true, 'warning', 'Expat kann nicht Deutsch sein');
      newAppointmentSubmit.textContent = 'Abschicken';
      isSubmitting = false;
      return;
    }

    const appointment: AppointmentSlot = {
      start: selectedTimeOption.dataset.start as string,
      end: selectedTimeOption.dataset.end as string
    };

    try {
      const response = await fetch(appointmentUrl + '/reschedule/internal', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          appointment_type: appointmentTypeSelect.value,
          appointment,
          calendar,
          contact_details: {
            first_name: formValues.first_name,
            last_name: formValues.last_name,
            email: formValues.email,
            landing_page_language: language.value,
            ...(formValues.phone_number && { phone_number: formValues.phone_number })
          },
          metadata: {
            product_type: product.value,
            ...(parametersObject.lead_id && { salesforce_lead_id: parametersObject.lead_id }),
            ...(parametersObject.opportunity_id && { salesforce_opportunity_id: parametersObject.opportunity_id }),
            ...(parametersObject.signup_service_id && { signup_service_id: parametersObject.signup_service_id }),
            ...(parametersObject.lead_service_id && { lead_service_id: parametersObject.lead_service_id }),
            ...(parametersObject.ottonova_tracking_id && { ottonova_tracking_id: parametersObject.ottonova_tracking_id })
          }
        })
      });
      if (!response.ok) {
        throw new Error(`${response.status}`);
      }

      const appointmentId = (await response.json()).appointment_id as string;

      updateAlert(rescheduleAppointmentAlert, true, 'success', 'Der Termin wurde erfolgreich gebucht');
      newAppointmentSubmit.textContent = 'Abschicken';

      const appointmentDetailsPromise = fetchAppointmentDetails(appointmentId);

      userAppointments.set(appointmentTypeSelect.value as AppointmentType, appointmentDetailsPromise);
      const newDetails = await appointmentDetailsPromise;

      if (newDetails) {
        removeAppointment();
        appointmentsWrapper.appendChild(createAppointmentItem(newDetails));
        updateSlides(true);
      }
    } catch (error: any) {
      let errorMessage = 'Es lag ein Problem beim Buchen des Termins vor: ' + (error.message || error);
      if (error.message === '422') {
        errorMessage = 'Zum Buchen eines Underwriting-Termins muss der Signup Schritt <i>underwriting_pending</i> oder <i>offer_underwriting_appointment</i> sein';
      }
      updateAlert(newConsultationAlert, true, 'danger', errorMessage);
      newAppointmentSubmit.textContent = 'Erneut versuchen';
      isSubmitting = false;
      console.error(error);
    }
  });

  newAppointmentForm.querySelectorAll('input').forEach(input => {
    input.addEventListener('input', () => {
      if (!newConsultationAlert.classList.contains('is-hidden') && !newConsultationAlert.classList.contains('force-keep-open')) {
        updateAlert(newConsultationAlert, false);
      }
    });
  });

  newAppointmentForm.querySelectorAll('select').forEach(select => {
    select.addEventListener('change', () => {
      if (!newConsultationAlert.classList.contains('is-hidden') && !newConsultationAlert.classList.contains('force-keep-open')) {
        updateAlert(newConsultationAlert, false);
      }
    });
  });
});

function salesforceProductToSalesforceServiceProduct(product: string | null): SalesforceServiceProduct {
  switch (product?.split('+').join('') as SalesforceProduct) {
    case SalesforceProduct.Comprehensive:
    case SalesforceProduct.ComprehensiveEmployeeOrSelfEmployed:
      return SalesforceServiceProduct.Comprehensive;
    case SalesforceProduct.ComprehensiveStudent:
      return SalesforceServiceProduct.ComprehensiveStudent;
    case SalesforceProduct.CivilServant:
      return SalesforceServiceProduct.CivilServant;
    case SalesforceProduct.CivilServantBroker:
      return SalesforceServiceProduct.CivilServantBroker;
    case SalesforceProduct.CivilServantApplicant:
      return SalesforceServiceProduct.CivilServantApplicant;
    case SalesforceProduct.ComprehensiveExpat:
      return SalesforceServiceProduct.ComprehensiveExpat;
    case SalesforceProduct.Dental:
      return SalesforceServiceProduct.Dental;
    default:
      return SalesforceServiceProduct.General;
  }
}

async function fetchAppointmentDetails(id: string): Promise<AppointmentDetails | null> {
  if (!id) {
    return null;
  }
  try {
    const response = await fetch(appointmentUrl + '/appointment-details/public/' + id);
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    if (!data.result || (data.result.status !== AppointmentStatus.Confirmed && data.result.status !== AppointmentStatus.Pending) || checkAppointmentDateBeforeCurrentDate(data.result)) {
      return null;
    }

    return data.result as AppointmentDetails;
  } catch (er) {
    return null;
  }
}

const currentDate = new Date().getTime();

function checkAppointmentDateBeforeCurrentDate(appointment: AppointmentDetails) {
  const appointmentStart = new Date(appointment?.start).getTime();
  return currentDate > appointmentStart;
}
